import * as yup from "yup";
import { PrioritySearchMatch, PrioritySearchSortKey, PrioritySearchSortOrder } from "../../graphql/graphql";
import { buildAvailablePrioritySearchFilters } from "./EditPriorityViewModal.utils";
export const EditTableViewInitialValues = {
    name: "",
    fields: {},
    sort: []
};
const MATCH_TYPES = Object.values(PrioritySearchMatch);
const MATCH_TYPE_REQUIRED_MESSAGE = "Match type is required";
// Field-specific validation schemas based on PrioritySearchWhereFilterInput
const fieldConfigValidationSchemas = {
    // age_years: AgeYearsInput
    age_years: yup.object({
        filterType: yup
            .string()
            .oneOf(["between", "older_than", "younger_than"])
            .required("Filter type is required"),
        between_min: yup.number().when("filterType", {
            is: "between",
            then: (schema) => schema.required("Minimum age is required"),
            otherwise: (schema) => schema.nullable()
        }),
        between_max: yup.number().when("filterType", {
            is: "between",
            then: (schema) => schema.required("Maximum age is required"),
            otherwise: (schema) => schema.nullable()
        }),
        older_than: yup.number().when("filterType", {
            is: "older_than",
            then: (schema) => schema.required("Age is required"),
            otherwise: (schema) => schema.nullable()
        }),
        younger_than: yup.number().when("filterType", {
            is: "younger_than",
            then: (schema) => schema.required("Age is required"),
            otherwise: (schema) => schema.nullable()
        })
    }),
    // condition_ids: PrioritySearchUuidWhereFilterObjectInput
    condition_ids: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        ids: yup
            .array()
            .of(yup.string())
            .min(1, "At least one condition must be selected")
            .required()
    }),
    // days_available: PrioritySearchWhereFilterObjectInput
    days_available: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        names: yup
            .array()
            .of(yup.string())
            .min(1, "At least one day must be selected")
            .required()
    }),
    needs_follow_up: yup.object({
        value: yup.boolean().required("Selection is required"),
        amount: yup
            .number()
            .typeError("Number is required")
            .integer("Number must be a whole number")
            .positive("Number must be greater than 0")
            .required("Number is required"),
        interval: yup
            .string()
            .oneOf(["years", "months", "weeks", "days"])
            .required("Interval is required")
    }),
    without_assigned_provider: yup.boolean().required("Selection is required"),
    // times_available: PrioritySearchWhereFilterObjectInput
    times_available: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        names: yup
            .array()
            .of(yup.string())
            .min(1, "At least one time must be selected")
            .required()
    }),
    // insurance_ids: PrioritySearchUuidWhereFilterObjectInput
    insurance_ids: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        ids: yup
            .array()
            .of(yup.string())
            .min(1, "At least one insurance must be selected")
            .required()
    }),
    // location: PriorityLocationInput
    location: yup.object({
        mode: yup
            .string()
            .oneOf(["zip", "states"])
            .required("Location filter type is required"),
        distance_in_miles: yup
            .number()
            .positive("Distance must be positive")
            .nullable()
            .when("mode", {
            is: "zip",
            then: (schema) => schema.required("Distance is required"),
            otherwise: (schema) => schema.notRequired().nullable()
        }),
        postal_codes: yup
            .array()
            .of(yup.string())
            .when("mode", {
            is: "zip",
            then: (schema) => schema
                .min(1, "At least one zip code must be provided")
                .required(),
            otherwise: (schema) => schema.notRequired().default([])
        }),
        state_ids: yup
            .array()
            .of(yup.string())
            .when("mode", {
            is: "states",
            then: (schema) => schema
                .min(1, "At least one state must be selected")
                .required(),
            otherwise: (schema) => schema.notRequired().default([])
        })
    }),
    // priority_status_id: PrioritySearchUuidWhereFilterObjectInput
    priority_status_id: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        ids: yup
            .array()
            .of(yup.string())
            .min(1, "At least one referral stage must be selected")
            .required()
    }),
    // search: String
    search: yup.string().min(1, "Search is required").required(),
    // tags: PrioritySearchWhereFilterObjectInput
    tags: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        names: yup
            .array()
            .of(yup.string())
            .min(1, "At least one day must be selected")
            .required()
    }),
    // therapy_category_ids: PrioritySearchUuidWhereFilterObjectInput
    therapy_category_ids: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        ids: yup
            .array()
            .of(yup.string())
            .min(1, "At least one therapy category must be selected")
            .required()
    }),
    // therapy_sub_category_ids: PrioritySearchUuidWhereFilterObjectInput
    therapy_sub_category_ids: yup.object({
        match: yup
            .string()
            .oneOf(MATCH_TYPES)
            .required(MATCH_TYPE_REQUIRED_MESSAGE),
        ids: yup
            .array()
            .of(yup.string())
            .min(1, "At least one therapy sub-category must be selected")
            .required()
    }),
    // time_waiting_date: TimeWaitingDateInput
    time_waiting_date: yup.object({
        filterType: yup
            .string()
            .oneOf(["between", "longer_than", "shorter_than"])
            .required("Filter type is required"),
        between_start: yup.string().when("filterType", {
            is: "between",
            then: (schema) => schema.required("Start date is required"),
            otherwise: (schema) => schema.nullable()
        }),
        between_end: yup.string().when("filterType", {
            is: "between",
            then: (schema) => schema.required("End date is required"),
            otherwise: (schema) => schema.nullable()
        }),
        between_interval: yup.string().when("filterType", {
            is: "between",
            then: (schema) => schema.required("Interval is required"),
            otherwise: (schema) => schema.nullable()
        }),
        longer_than: yup.string().when("filterType", {
            is: "longer_than",
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.nullable()
        }),
        longer_than_interval: yup.string().when("filterType", {
            is: "longer_than",
            then: (schema) => schema.required("Interval is required"),
            otherwise: (schema) => schema.nullable()
        }),
        shorter_than: yup.string().when("filterType", {
            is: "shorter_than",
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.nullable()
        }),
        shorter_than_interval: yup.string().when("filterType", {
            is: "shorter_than",
            then: (schema) => schema.required("Interval is required"),
            otherwise: (schema) => schema.nullable()
        })
    })
};
const sortSchema = yup
    .array()
    .of(yup.object({
    field: yup
        .string()
        .oneOf(Object.values(PrioritySearchSortKey))
        .required(),
    order: yup
        .string()
        .oneOf(Object.values(PrioritySearchSortOrder))
        .required()
}))
    .optional()
    .default([]);
// Function to create a validation schema that only validates enabled fields
export const createEditTableViewSchema = (enabledFieldIds) => {
    // Create a dynamic fields schema that only includes enabled fields
    const fieldsSchema = {};
    enabledFieldIds.forEach((fieldId) => {
        const validationSchema = fieldConfigValidationSchemas[fieldId];
        if (validationSchema) {
            fieldsSchema[fieldId] = validationSchema;
        }
    });
    return yup.object({
        name: yup.string(),
        fields: yup.object().shape(fieldsSchema),
        sort: sortSchema
    });
};
// Default schema for backward compatibility
export const EditTableViewSchema = createEditTableViewSchema([]);
// Re-export the utility functions for convenience
export { buildAvailablePrioritySearchFilters };
